/* eslint-disable */
import { ReactElement, useState } from "react";
import { Tooltip } from 'antd';
import { EyeOutlined, FileAddOutlined, MailOutlined } from "@ant-design/icons";
import * as React from "react";
import { dateValidation } from "../helpers/dateValidation";
import { useGridPagination } from "../helpers/useGridPagination";
import ResponsiveGrid from '../../../../../../../../Components/TableMui/ResponsiveGrid';

export type MailItem = {
    dataRecordId: number
    documentType: string | null,
    dateProcessed: string | null,
    dateSubmitted: string | null,
    url: string | null,
    deliveryType: string | null,
}

const ButtonDocumentUploader = (props: { item: MailItem, buttonCallback: (dataRecordId: number) => Promise<string> }): ReactElement => {
    const [status, setStatus] = useState("");
    const [fileUrl, setFileUrl] = useState("");
    if (status === "done") {
        return (
            <Tooltip title="View document">
                <a
                    className="ric-icon-btn ric-icon-btn--upload"
                    data-testid={`ric-mail-view-btn-${props.item.dataRecordId}`}
                    aria-label="View document"
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <EyeOutlined style={{ fontSize: "16px" }} />
                </a>
            </Tooltip>
        )
    }

    if (status === "pending") {
        return (
            <span className="ric-doc-cell ric-doc-status" aria-live="polite">
                <span className="ric-doc-spinner" aria-hidden="true" />
                <span>Downloading...</span>
            </span>
        )
    }

    return (
        <Tooltip title="Download the document. Once ready, click again to view it.">
            <button
                type="button"
                data-testid={`ric-mail-upload-btn-${props.item.dataRecordId}`}
                className="ric-icon-btn ric-icon-btn--upload"
                aria-label="Download document"
                onClick={async () => {
                    setStatus("pending");
                    const url = await props.buttonCallback(props.item.dataRecordId);
                    if (!url || url.startsWith("Error")) {
                        setStatus("");
                        return;
                    }
                    setFileUrl(url);
                    setStatus("done");
                }}
            >
                <FileAddOutlined style={{ fontSize: "16px" }} />
            </button>
        </Tooltip>
    )
}

const DATE_SORT_FIELDS = {
    dateProcessed: "dateProcessed",
    dateSubmitted: "dateSubmitted",
};

const MailArhive = (props: { mailItems: MailItem[], openFileCallback: (dataRecordId: number) => Promise<string> }): ReactElement => {
    const { mailItems, openFileCallback } = props;

    const rows = React.useMemo(() => mailItems ?? [], [mailItems]);
    const { pagedRows, server } = useGridPagination(rows, { dateFields: DATE_SORT_FIELDS });

    const columns = React.useMemo(() => [
        { field: "documentType", label: "Document Type", flex: 1.4, minWidth: 150 },
        { field: "deliveryType", label: "Delivery Type", flex: 1, minWidth: 130 },
        { field: "dateProcessed", label: "Date Processed", flex: 1, minWidth: 130, render: (value) => dateValidation(value) },
        { field: "dateSubmitted", label: "Date Submitted", flex: 1, minWidth: 130, render: (value) => dateValidation(value) },
        {
            field: "url",
            label: "Document",
            flex: 1,
            minWidth: 150,
            align: "center" as const,
            sortable: false,
            render: (_value: any, row: MailItem) => (
                <ButtonDocumentUploader item={row} buttonCallback={openFileCallback} />
            ),
        },
    ], [openFileCallback]);

    const gridRef = React.useRef<HTMLDivElement>(null);
    const [visible, setVisible] = React.useState(true);

    React.useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => setVisible(el.offsetWidth > 0));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={gridRef} className="ric-pane">
            <ResponsiveGrid
                key={visible ? "visible" : "hidden"}
                columns={columns}
                rows={pagedRows}
                getRowId={(row) => row.dataRecordId}
                emptyText="No data."
                emptyIcon={<MailOutlined />}
                server={server}
            />
        </div>
    )
}
export default MailArhive;
